/**
 * ReasonCell - why the row is where it is, in plain words (docs/plans/crm-recruitment-list-plan.md
 * Part A section 5, B4): "Replied 2 h ago, waiting on us", "Callback 3:15 PM", "New lead, came in
 * 3 h ago", "Wrote 2 d ago, gone quiet", "No answer yet, try 3 of 6", "No activity 6 d", "Next try
 * Fri". The words are recruitLogic reasonWords, the one place they are written. The weight says
 * the attention tier (backend services/crmAttention.js, owner 2026-09-28): a needs-action-now row
 * is strong, a keep-trying row plain, and a row with nothing due yet dimmed.
 */
import { useMemo } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTeam } from '../../../lib';
import { useNow } from '../../../useNow';
import { reasonWords, rowTier } from '../../recruitLogic';
import type { RecruitReason, RecruitRow, RecruitStudy } from '../../types';

export interface ReasonCellProps {
  row: RecruitRow;
  /** The study the list is for: its cadence ceiling words "try 3 of 6". */
  study: Pick<RecruitStudy, 'max_attempts'>;
  className?: string;
}

/** A row the server sent without a reason reads as open (never a crash). */
const NO_REASON: RecruitReason = { kind: 'open', at: null, rank: 480, tier: 3 };

/** Kinds that are somebody waiting on us right now: a dot draws the eye. */
const URGENT: ReadonlySet<string> = new Set(['needs_reply', 'callback_due', 'agent_review']);

/** The text weight per attention tier: needs action now, keep trying, nothing due. */
const TIER_TEXT: Record<number, string> = {
  1: 'font-medium text-gray-900 dark:text-gray-100',
  2: 'text-gray-600 dark:text-gray-300',
  3: 'text-gray-400 dark:text-gray-500',
};

export function ReasonCell({ row, study, className = '' }: ReasonCellProps) {
  const { user } = useAuth();
  const meId = user?.id ?? null;
  const { byId } = useTeam();
  const nowMs = useNow(15000);
  const reason = row.reason || NO_REASON;
  const maxAttempts = study.max_attempts;

  const words = useMemo(
    () => reasonWords(reason, new Date(nowMs), {
      meId,
      nameOf: (id: string) => byId.get(id)?.name ?? null,
      maxAttempts,
    }),
    [reason, nowMs, meId, byId, maxAttempts],
  );

  const tier = rowTier(reason);
  return (
    <span
      title={words}
      className={`flex min-w-0 items-center gap-1.5 text-xs ${TIER_TEXT[tier] || TIER_TEXT[3]} ${className}`}
    >
      {URGENT.has(reason.kind) && <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-500" aria-hidden="true" />}
      <span className="truncate">{words}</span>
    </span>
  );
}

export default ReasonCell;
